import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Button, OverlayTrigger, Popover } from "react-bootstrap";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { RichTextEditor } from "../../RichTextEditor";
import { UserHover } from "../../UserHover";
import { authService, newAuthService } from "../../../Service/Axios";
import {
  APPLICATION_EMAIL_LIST_URL,
  CREATE_EMAIL_URL,
  GET_APPLICANT_OVERVIEW_EMAIl_URL,
  GET_EMAIL_DETAILS_URL,
  GLOBAL_EMAIL_LIST_URL,
  GLOBAL_FILE_UPLOAD_URL,
} from "../../../Constant/api";
import { DEFAULT_MODULE_TYPE } from "../../../Constant/constants";
import { useEmailTemplateList } from "../../../Constant/apiHooks";
import {
  convertHTMLToText,
  downloadFile,
  formatDateWithShortMonth,
  getFirstLetterOfName,
} from "../../../Utils";

export const EmailInbox = ({ gid, is360View }) => {
  const { t } = useTranslation();

  const [activeEmail, setActiveEmail] = useState(null);
  const [emailContent, setEmailContent] = useState("");
  const [emailList, setEmailList] = useState([]);
  const [emailAttachment, setEmailAttachment] = useState([]);
  const [onComposeEmail, setOnComposeEmail] = useState(false);
  const [showInboxDropdown, setShowInboxDropdown] = useState(false);

  const [formData, setFormData] = useState({
    emailSubject: "",
    emailTo: "",
    emailCc: "",
  });
  const { emailSubject, emailTo, emailCc } = formData;

  const { allEmailTemplateList } = useEmailTemplateList();

  // Toggle
  const toggleComposeEmail = () => {
    onReset();
    setOnComposeEmail(!onComposeEmail);
  };
  const toggleInboxDropdown = () => {
    setShowInboxDropdown(!showInboxDropdown);
  };

  // Listing
  const getEmailList = async () => {
    const payload = {};
    if (is360View) {
      payload.userId = gid;
    } else if (gid) {
      payload.taskId = gid;
    }
    let apiUrl = "";
    if (is360View) {
      apiUrl = GET_APPLICANT_OVERVIEW_EMAIl_URL;
    } else if (gid) {
      apiUrl = APPLICATION_EMAIL_LIST_URL;
    } else {
      apiUrl = GLOBAL_EMAIL_LIST_URL;
    }
    const response = await newAuthService(apiUrl, "POST", payload);
    if (response.status) {
      setEmailList(response.data);
    } else {
      setEmailList([]);
    }
  };

  useEffect(() => {
    getEmailList();
  }, []);

  // On Change
  const handleChange = (e) => {
    if (e.target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    }
  };

  const handleChangeEmail = (value) => {
    setEmailContent(value);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    const fileParts = file.type.split("/");
    const fileType = fileParts[1];
    const fileName = file.name;
    let fileSize = 0;
    if (file.size < 1000000) {
      fileSize = Math.floor(file.size / 1000) + " " + "KB";
    } else {
      fileSize = Math.floor(file.size / 1000000) + " " + "MB";
    }
    if (fileType === "pdf") {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("appType", DEFAULT_MODULE_TYPE.email);
      const response = await authService(
        GLOBAL_FILE_UPLOAD_URL,
        "POST",
        formData
      );
      if (response) {
        const newUpload = {
          gid: uuid(),
          name: fileName,
          path: response,
          size: fileSize,
          filetype: fileType,
        };
        setEmailAttachment([...emailAttachment, newUpload]);
      }
    }
  };

  // On Click
  const onViewEmail = async (id) => {
    const data = {
      gid: id,
    };
    const response = await authService(GET_EMAIL_DETAILS_URL, "POST", data);
    setActiveEmail(response);
    setOnComposeEmail(false);
    setFormData((prev) => ({
      ...prev,
      emailTo: response.to,
      emailCc: response.cc,
    }));
  };

  const handleSelectTemplate = (template) => {
    setEmailContent(template);
    toggleInboxDropdown();
  };

  // Submit
  const onSendEmail = async () => {
    try {
      const data = {
        taskId: gid,
        to: emailTo,
        cc: emailCc,
        subject: emailSubject,
        content: emailContent,
        file: emailAttachment,
      };
      const response = await authService(CREATE_EMAIL_URL, "POST", data);
      if (response.success) {
        onReset();
        getEmailList();
        toast.success(t("common.messages.notification.success.createEmail"));
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    }
  };

  // Reset
  const onReset = () => {
    setFormData({
      emailSubject: "",
      emailTo: "",
      emailCc: "",
    });
    setEmailContent("");
    setEmailAttachment([]);
    setActiveEmail(null);
    setOnComposeEmail(false);
  };

  return (
    <div>
      {!onComposeEmail && !activeEmail && (
        <div className="">
          <div className="inbox-search px-3 py-3 position-sticky top-12 bg-white z-10">
            <div className="row">
              <div className="col">
                <div className="">
                  <div className="input-group">
                    <span className="input-group-text bg-transparent">
                      <i className="pluto-search"></i>
                    </span>
                    <input
                      type="search"
                      className="form-control"
                      placeholder={t("common.placeHolder.searchEmail")}
                    />
                  </div>
                </div>
              </div>
              <div className="col-auto ps-2">
                <button
                  type="submit"
                  className="btn btn-secondary"
                  onClick={toggleComposeEmail}
                >
                  {t("common.button.compose")}
                </button>
              </div>
            </div>
          </div>
          {/* Email List */}
          {!activeEmail && !onComposeEmail && (
            <div className="email-details">
              <div className="email-inbox-list bg-white">
                <ul>
                  {emailList &&
                    emailList.length > 0 &&
                    emailList.map((email, index) => (
                      <li
                        key={`${email.id}${index}`}
                        onClick={() => onViewEmail(email.gid)}
                      >
                        <div className="in-box">
                          <div className="time-view">
                            {email.sendDate &&
                              formatDateWithShortMonth(email.sendDate)}
                          </div>
                          <div className="inbox-short-details d-flex align-items-start">
                            <div className="emailer-details">
                              {/* <div className="emailer-avatar-name d-flex align-items-center hover-effect position-relative cursor-pointer mb-2">
                                <div className="img-box me-2">
                                  {email.photoURL ? (
                                    <img
                                      src={email.photoURL}
                                      alt={`${email.firstName}${email.lastName}`}
                                    />
                                  ) : (
                                    getFirstLetterOfName(email)
                                  )}
                                </div>
                                <div className="emailer-name">
                                  {email.firstName} {email.lastName}
                                </div>
                                <UserHover user={email} />
                              </div> */}
                              <OverlayTrigger
                                placement="right"
                                overlay={
                                  <Popover className="max-w-unset">
                                    <UserHover user={email} />
                                  </Popover>
                                }
                              >
                                <div className="d-flex align-items-center mb-2 cursor-pointer">
                                  <div className="w-8 h-8 rounded-circle overflow-hidden">
                                    {email.photoURL ? (
                                      <div className="img-box w-100 h-100">
                                        <img
                                          src={email.photoURL}
                                          alt={`${email.firstName} ${email.lastName}`}
                                          className="w-100 h-100 object-fit-cover"
                                        />
                                      </div>
                                    ) : (
                                      getFirstLetterOfName(email)
                                    )}
                                  </div>

                                  <div className="avatar-short-dtls ms-2">
                                    <h4 className="text-sm font-medium text-slate-600 mb-0 text-nowrap">
                                      {email.firstName} {email.lastName}
                                    </h4>
                                  </div>
                                </div>
                              </OverlayTrigger>
                            </div>
                          </div>
                          <div className="inbox-title">{email.subject}</div>
                          <div className="inbox-short-content">
                            <p>
                              {email.content &&
                                convertHTMLToText(email.content)}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
          {/* Email List */}
        </div>
      )}
      {activeEmail && (
        <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 p-0 email-details">
          <div className="email-details-box border-start border-end border-bottom">
            <div className="email-header px-5 py-3 bg-light">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-back btn-tranparent d-flex justify-content-center align-items-center border-0 rounded-circle"
                  onClick={onReset}
                >
                  <i className="bx bx-arrow-back bx-sm"></i>
                </button>
                <h3 className="h6 ms-1 mb-0">{activeEmail.subject}</h3>
                <Dropdown
                  className="user-email-dropdown ms-auto"
                  show={showInboxDropdown}
                  onToggle={toggleInboxDropdown}
                >
                  <Dropdown.Toggle variant="light">
                    <i className="bx bx-dots-vertical-rounded"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="dropdown-menu-end dropdown-custom-shadow">
                    <div className="pt-0 px-3 template-search-title">
                      {t("common.label.template")}
                    </div>
                    <div className="p-3 bg-light">
                      <div className="search-div px-0 pb-2">
                        <div className="input-group">
                          <input
                            type="search"
                            className="form-control"
                            placeholder={t("common.placeHolder.searchTemplate")}
                          />
                          <button type="button" className="input-group-text">
                            <i className="pluto-search"></i>
                          </button>
                        </div>
                      </div>
                      <div className="template-search-list">
                        {allEmailTemplateList &&
                          allEmailTemplateList.length > 0 &&
                          allEmailTemplateList.map((template) => (
                            <Button
                              variant="template"
                              type="button"
                              onClick={() =>
                                handleSelectTemplate(template.template)
                              }
                            >
                              {template.name}
                            </Button>
                          ))}
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>

            <div className="email-body px-3 py-3">
              <div className="emailer-details">
                <div className="emailer-to">
                  {t("common.label.to")}: <strong>{activeEmail.to}</strong>
                </div>
                <div className="email-subject">
                  {t("common.label.subject")}:{" "}
                  <strong>{activeEmail.subject}</strong>
                </div>
              </div>
              <div className="email-content-area">
                <div
                  dangerouslySetInnerHTML={{ __html: activeEmail.content }}
                />
                <br />
                <br />
                {activeEmail.files &&
                  activeEmail.files.length > 0 &&
                  activeEmail.files.map((et) => (
                    <div className="attache-box">
                      <div className="attache-icon">
                        <i className="bx bxs-file-pdf"></i>
                      </div>
                      <div className="attache-details">
                        <div className="attach-filename">{et.name}</div>
                        <small>{et.size}</small>
                        <button
                          type="button"
                          className="btn-close close-icon"
                          onClick={() => downloadFile(et.url, et.name)}
                        >
                          <i className="bx bxs-download"></i>
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div className="email-footer">
              <div className="row align-items-center gy-4">
                <div className="col-auto ms-md-auto">
                  <ul className="email-btn-list">
                    <li>
                      <button
                        type="button"
                        className="btn btn-transparent"
                        onClick={onReset}
                      >
                        {t("common.button.cancel")}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {onComposeEmail && (
        <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12 p-0 email-details">
          <div className="email-details-box">
            <div className="email-header px-5 py-3 bg-light">
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-back bg-transparent d-flex justify-content-center align-items-center border-0"
                  onClick={onReset}
                >
                  <i className="bx bx-arrow-back bx-sm"></i>
                </button>
                {t("auth.email.inbox.title")}
                <Dropdown
                  className="user-email-dropdown ms-auto"
                  show={showInboxDropdown}
                  onToggle={toggleInboxDropdown}
                >
                  <Dropdown.Toggle variant="light">
                    <i className="bx bx-dots-vertical-rounded"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="dropdown-menu-end dropdown-custom-shadow">
                    <div className="pt-0 px-3 template-search-title">
                      {t("common.label.template")}
                    </div>
                    <div className="p-3 bg-light">
                      <div className="search-div px-0 pb-2">
                        <div className="input-group">
                          <input
                            type="search"
                            className="form-control"
                            placeholder={t("common.placeHolder.searchTemplate")}
                          />
                          <button type="button" className="input-group-text">
                            <i className="pluto-search"></i>
                          </button>
                        </div>
                      </div>
                      <div className="template-search-list">
                        {allEmailTemplateList &&
                          allEmailTemplateList.length > 0 &&
                          allEmailTemplateList.map((template) => (
                            <Button
                              variant="template"
                              type="button"
                              onClick={() =>
                                handleSelectTemplate(template.template)
                              }
                            >
                              {template.name}
                            </Button>
                          ))}
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <div className="email-body px-5 py-3">
              <div className="email-content-area pt-0">
                <div className="mb-3">
                  <label className="form-label" htmlFor="text">
                    {t("common.label.to")}
                  </label>
                  <input
                    type="text"
                    className="email-subject form-control plutoinput-form-control"
                    name="emailTo"
                    value={emailTo}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="text">
                    {t("common.label.cc")}
                  </label>
                  <input
                    type="text"
                    className="email-subject form-control plutoinput-form-control"
                    name="emailCc"
                    value={emailCc}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="text">
                    {t("common.label.subject")}
                  </label>
                  <input
                    type="text"
                    className="email-subject form-control plutoinput-form-control"
                    name="emailSubject"
                    value={emailSubject}
                    onChange={handleChange}
                  />
                </div>

                <RichTextEditor
                  value={emailContent}
                  onChange={handleChangeEmail}
                  onUpload={(e) => handleUpload(e, "emailAttachment")}
                />

                {emailAttachment &&
                  emailAttachment.length > 0 &&
                  emailAttachment.map((et) => (
                    <div className="attache-box">
                      <div className="attache-icon">
                        <i className="bx bxs-file-pdf"></i>
                      </div>
                      <div className="attache-details">
                        <div className="attach-filename">{et.name}</div>
                        <small>{et.size}</small>
                        <button type="button" className="btn-close close-icon">
                          <i className="bx bx-x"></i>
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div className="email-footer">
              <div className="row align-items-center gy-4">
                <div className="col-auto ms-md-auto">
                  <ul className="email-btn-list">
                    <li>
                      <button
                        type="button"
                        className="btn btn-transparent"
                        onClick={toggleComposeEmail}
                      >
                        {t("common.button.cancel")}
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={onSendEmail}
                      >
                        <i className="bx bx-send me-1"></i>
                        {t("common.button.send")}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
